/**
 * Default geocoder: place name -> (lat, lon) via OpenStreetMap Nominatim, with a
 * storage-backed cache. (Port of lib/geocoding.py.)
 *
 * `lib/event_intake` deliberately keeps geocoding a pluggable seam; this module is the
 * default provider the app installs so the seam is actually FILLED in the running product —
 * before this, place names silently resolved to nothing and the UI's promise of automatic
 * geocoding was never true.
 *
 * Design constraints:
 *   - browser fetch (Python used stdlib urllib), no API key — Nominatim's free endpoint with
 *     a descriptive User-Agent per its usage policy. Lookups happen only on explicit form
 *     submits, so volume is trivially within the 1 req/s policy.
 *   - every successful lookup is cached in the storage backend at the same logical path the
 *     Python version used on disk (users/_geocode_cache.json), so a place is fetched once per
 *     browser and the app keeps working offline for known places;
 *   - failures (offline, rate-limited, unknown place) return None — callers degrade
 *     honestly (the event keeps its place NAME but no coordinates) rather than crashing.
 *
 * DIVERGENCE from Python: fetch is async, so every lookup function returns a Promise (and
 * the event_intake geocoder seam is typed accordingly).
 */

import { USERS_DIR } from "./config";
import { getStorage } from "./storage";
import { set_geocoder } from "./event_intake";

export const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";
const _USER_AGENT = "BirthTimeRectificationScript/1.0 (local desktop app)";
const _TIMEOUT_SECONDS = 6.0;

export const GEOCODE_CACHE_PATH = `${USERS_DIR}/_geocode_cache.json`;

let _memory_cache: Record<string, Record<string, any>> | null = null;

function _cache_key(name: string): string {
  return String(name).trim().toLowerCase().split(/\s+/).filter(Boolean).join(" ");
}

function _load_cache(): Record<string, Record<string, any>> {
  if (_memory_cache === null) {
    const raw = getStorage().read(GEOCODE_CACHE_PATH);
    if (raw === null) {
      _memory_cache = {};
    } else {
      try {
        const data = JSON.parse(raw);
        _memory_cache = typeof data === "object" && data !== null && !Array.isArray(data) ? data : {};
      } catch {
        _memory_cache = {};
      }
    }
  }
  return _memory_cache!;
}

function _save_cache(cache: Record<string, Record<string, any>>): void {
  try {
    getStorage().write(GEOCODE_CACHE_PATH, JSON.stringify(cache, null, 1));
  } catch {
    // a cache that can't persist is still a working memory cache
  }
}

/** Test seam: forget the in-memory cache so the next lookup re-reads storage. */
export function _reset_geocode_memory_cache(): void {
  _memory_cache = null;
}

type NominatimLookup = (name: string) => Promise<Record<string, any> | null>;

/** One uncached Nominatim query. Returns {lat, lon, display_name} or None. */
async function _nominatim_lookup_impl(name: string): Promise<Record<string, any> | null> {
  const query = new URLSearchParams({ q: name, format: "jsonv2", limit: "1" });
  let results: any;
  try {
    const response = await fetch(`${NOMINATIM_URL}?${query.toString()}`, {
      headers: { "User-Agent": _USER_AGENT, "Accept-Language": "en" },
      signal: AbortSignal.timeout(_TIMEOUT_SECONDS * 1000),
    });
    results = await response.json();
  } catch {
    return null;
  }
  if (!Array.isArray(results) || results.length === 0) {
    return null;
  }
  const first = results[0];
  const lat = Number(first?.["lat"]);
  const lon = Number(first?.["lon"]);
  if (first?.["lat"] === undefined || first?.["lon"] === undefined || Number.isNaN(lat) || Number.isNaN(lon)) {
    return null;
  }
  return {
    lat,
    lon,
    display_name: String(first["display_name"] || name),
  };
}

let _nominatim_lookup: NominatimLookup = _nominatim_lookup_impl;

/** Test seam (Python tests monkeypatched geocoding._nominatim_lookup). null restores the default. */
export function _set_nominatim_lookup(fn: NominatimLookup | null): void {
  _nominatim_lookup = fn ?? _nominatim_lookup_impl;
}

/**
 * Resolve a place name to {lat, lon, display_name}, cache-first. None when the name is
 * empty, unknown, or the network is unavailable AND the place was never cached.
 */
export async function geocode_with_display(name: string | null | undefined): Promise<Record<string, any> | null> {
  if (!name || !String(name).trim()) {
    return null;
  }
  const key = _cache_key(name);
  const cache = _load_cache();
  const hit = cache[key];
  if (typeof hit === "object" && hit !== null && !Array.isArray(hit) && "lat" in hit && "lon" in hit) {
    return hit;
  }
  const result = await _nominatim_lookup(String(name).trim());
  if (result !== null) {
    cache[key] = result;
    _save_cache(cache);
  }
  return result;
}

/** The `set_geocoder`-shaped adapter: name -> (lat, lon) or None. */
export async function default_geocoder(name: string): Promise<[number, number] | null> {
  const result = await geocode_with_display(name);
  return result ? [result["lat"], result["lon"]] : null;
}

/** Fill the event-intake geocoding seam with this provider. Idempotent. */
export function install_default_geocoder(): void {
  set_geocoder(default_geocoder);
}
